import { fetchWithAuth } from 'utils/fetchWithAuth';
import { ApiError, unwrapResponse } from 'utils/unwrapResponse';

// Mirrors backend/constants/tutorialCategories.ts; backend/tests/tutorialsContract.test.ts fails on drift.
export const TUTORIAL_CONTENT_TYPES = ['video/mp4', 'video/webm'] as const;
export type TutorialContentType = (typeof TUTORIAL_CONTENT_TYPES)[number];
export const TUTORIAL_MAX_BYTES = 2 * 1024 ** 3;
export const TUTORIAL_TITLE_MAX = 120;
export const TUTORIAL_DESCRIPTION_MAX = 500;
export const OTHER_TUTORIAL_CATEGORY = 'other';
export const TUTORIAL_ACCEPT = TUTORIAL_CONTENT_TYPES.join(',');

export interface TutorialVideo {
  id: string;
  title: string;
  description: string;
  url: string;
  downloadUrl: string;
  thumbnailUrl: string | null;
  createdAt: string;
  size: number;
}

export type TutorialDetails = Pick<TutorialVideo, 'title' | 'description'>;

export interface TutorialCategoryGroup {
  key: string;
  videos: TutorialVideo[];
}

export interface TutorialUploadTicket {
  uploadUrl: string;
  thumbnailUploadUrl: string;
  blobName: string;
}

export interface TutorialUploadRequest {
  title: string;
  description: string;
  category: string;
  contentType: TutorialContentType;
  size: number;
}

const BASE = 'admin/tutorials';
const UPLOAD_BLOCK_BYTES = 8 * 1024 ** 2;
const THUMBNAIL_UPLOAD_TIMEOUT_MS = 10_000;

export const isTutorialContentType = (type: string): type is TutorialContentType =>
  TUTORIAL_CONTENT_TYPES.some((allowed) => allowed === type);

export const tutorialFileError = (file: File): 'type' | 'empty' | 'size' | null => {
  if (!isTutorialContentType(file.type)) return 'type';
  if (file.size === 0) return 'empty';
  if (file.size > TUTORIAL_MAX_BYTES) return 'size';
  return null;
};

export const tutorialsApi = {
  list: async (): Promise<TutorialCategoryGroup[]> => {
    const data = await unwrapResponse<{ categories: TutorialCategoryGroup[] }>(
      await fetchWithAuth(BASE),
      'Failed to load the tutorials'
    );
    return data?.categories ?? [];
  },

  requestUpload: async (body: TutorialUploadRequest): Promise<TutorialUploadTicket> =>
    unwrapResponse<TutorialUploadTicket>(
      await fetchWithAuth(`${BASE}/upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
      'Failed to prepare the upload'
    ),

  // The id is `prefix/file`: the slash must stay a slash, so it is not URL-encoded
  update: async (id: string, details: TutorialDetails): Promise<void> => {
    await unwrapResponse<void>(
      await fetchWithAuth(`${BASE}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(details),
      }),
      'Failed to save the tutorial'
    );
  },

  remove: async (id: string): Promise<void> => {
    try {
      await unwrapResponse<void>(await fetchWithAuth(`${BASE}/${id}`, { method: 'DELETE' }), 'Failed to delete the video');
    } catch (error) {
      // Already deleted counts as deleted
      if (!(error instanceof ApiError && error.status === 404)) throw error;
    }
  },
};

// Lazy: keeps the Azure SDK out of the main bundle, loaded only when a superadmin uploads
const blockBlobClient = async (sasUrl: string) => {
  const { BlockBlobClient } = await import('@azure/storage-blob');
  return new BlockBlobClient(sasUrl);
};

export const uploadTutorialFile = async (
  ticket: TutorialUploadTicket,
  file: File,
  { title, description }: TutorialDetails,
  onProgress: (ratio: number) => void,
  signal: AbortSignal
): Promise<void> => {
  // Small blocks: the browser client reports progress per request, never during one single-shot PUT
  await (await blockBlobClient(ticket.uploadUrl)).uploadData(file, {
    blockSize: UPLOAD_BLOCK_BYTES,
    maxSingleShotSize: UPLOAD_BLOCK_BYTES,
    blobHTTPHeaders: { blobContentType: file.type },
    metadata: { title: encodeURIComponent(title), ...(description && { description: encodeURIComponent(description) }) },
    onProgress: ({ loadedBytes }) => onProgress(loadedBytes / file.size),
    abortSignal: signal,
  });
};

export const uploadTutorialThumbnail = async (ticket: TutorialUploadTicket, thumbnail: Blob): Promise<void> => {
  await (await blockBlobClient(ticket.thumbnailUploadUrl)).uploadData(thumbnail, {
    blobHTTPHeaders: { blobContentType: 'image/jpeg' },
    // The video is already stored: the SDK's retries must not hold the dialog (no limit before Safari 16)
    abortSignal: AbortSignal.timeout?.(THUMBNAIL_UPLOAD_TIMEOUT_MS),
  });
};
